import React, { useRef, useState, useEffect } from 'react';
import {
    FaBold,
    FaItalic,
    FaUnderline,
    FaListUl,
    FaListOl,
    FaAlignLeft,
    FaAlignCenter,
    FaAlignRight,
    FaAlignJustify,
    FaTrash,
    FaChevronDown,
    FaChevronUp,
    FaFileAlt,
    FaRegEdit
} from 'react-icons/fa';
import './notes-editor.css';
import { ConfirmAlert } from '../alert/ConfirmAlert';
import Button from '../buttons/Button';
import ActionButton from '../buttons/ActionButton';
import { convertHtmlToJson, convertJsonToHtml } from '../../../utils/note-format-utils';

const NotesParentsSection = ({
    title = "Notes for parents",
    placeholder = "Write a message for parents...",
    initialValue = '',
    onSave,
    isSaving = false
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState(initialValue);
    const editorRef = useRef(null);
    const isSettingContent = useRef(false);

    // Convert the incoming JSON (or legacy HTML) to standard HTML
    const initialHtml = React.useMemo(() => convertJsonToHtml(initialValue), [initialValue]);

    // Sync state and editor content when initialValue loads from API
    useEffect(() => {
        setContent(initialHtml);
        // If there is no initialHtml, default to edit mode. Otherwise view mode.
        if (!initialHtml || initialHtml.trim() === '') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
        // Auto-expand if there is saved note content
        if (initialHtml && initialHtml.trim() !== '') {
            setIsExpanded(true);
        }
    }, [initialHtml]);

    // Ensure the editor content is restored if the dropdown is closed and re-opened while in edit mode
    useEffect(() => {
        if (isExpanded && isEditing && editorRef.current) {
            // Only set if empty to avoid overwriting ongoing edits
            if (editorRef.current.innerHTML === '') {
                isSettingContent.current = true;
                editorRef.current.innerHTML = content || '';
                isSettingContent.current = false;
            }
        }
    }, [isExpanded, isEditing]);

    const handleInput = () => {
        if (isSettingContent.current) return;
        if (editorRef.current) {
            const html = editorRef.current.innerHTML;
            // Handle browser-specific empty states (like single <br>)
            const normalized = html === '<br>' || html === '<div><br></div>' ? '' : html;
            setContent(normalized);
        }
    };

    // Use onMouseDown + e.preventDefault to stop toolbar buttons from stealing editor selection focus
    const handleFormat = (e, command, value = null) => {
        e.preventDefault();
        document.execCommand(command, false, value);
        // Force sync with state after command executes
        handleInput();
        if (editorRef.current) {
            editorRef.current.focus();
        }
    };

    const handleClearClick = (e) => {
        e.preventDefault();
        if (editorRef.current) {
            editorRef.current.innerHTML = '';
            setContent('');
            editorRef.current.focus();
        }
    };

    const handleCancel = () => {
        if (initialHtml && initialHtml.trim() !== '') {
            setIsEditing(false);
        } else {
            setIsExpanded(false);
            setIsEditing(false);
        }
        setContent(initialHtml);
    };

    const handleSave = () => {
        // Strip tags to check if it's essentially empty
        const stripped = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        const finalHtml = stripped === '' ? '' : content;
        
        // Convert the HTML DOM to JSON array string
        const finalJson = finalHtml === '' ? '' : convertHtmlToJson(finalHtml);

        onSave(finalJson);
    };

    const handleDeleteClick = async (e) => {
        e.preventDefault();
        const result = await ConfirmAlert({ 
            confirmBtnText: 'Delete note', 
            text: "Are you sure you want to delete this note? This action cannot be undone." 
        });
        if (result.isConfirmed) {
            confirmDelete();
        }
    };

    const confirmDelete = () => {
        onSave('');
        setIsExpanded(false);
        setIsEditing(false);
    };

    const handleStartEdit = () => {
        setIsEditing(true);
        // Defer innerHTML initialization to let React render the editor elements first
        setTimeout(() => {
            if (editorRef.current) {
                isSettingContent.current = true;
                editorRef.current.innerHTML = content || '';
                isSettingContent.current = false;
                editorRef.current.focus();
            }
        }, 0);
    };

    const getCleanPreviewText = (html) => {
        if (!html) return '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const cleanText = temp.textContent || temp.innerText || '';
        return cleanText.length > 60 ? cleanText.substring(0, 60) + '...' : cleanText;
    };

    const handleToggleExpand = () => {
        const nextExpanded = !isExpanded;
        setIsExpanded(nextExpanded);
        
        if (nextExpanded && (!initialHtml || initialHtml.trim() === '')) {
            setIsEditing(true);
        }
    };

    const isDirty = content !== initialHtml;

    return (
        <div className="w-full mb-6">
            {/* Accordion Header */}
            <div
                className={`notes-accordion-header flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-100/80 transition-colors select-none ${isExpanded ? 'expanded' : ''}`}
                onClick={handleToggleExpand}
            >
                <div className="flex items-center gap-3">
                    <FaFileAlt className="text-[#ED1C24] text-lg" />
                    <span className="font-semibold text-gray-800 text-base">
                        {title}
                        {!isExpanded && initialHtml && (
                            <span className="text-gray-400 text-sm font-normal ml-2">
                                — {getCleanPreviewText(initialHtml)}
                            </span>
                        )}
                    </span>
                </div>
                <div>
                    {isExpanded ? (
                        <FaChevronUp className="text-gray-500 hover:text-gray-700" />
                    ) : (
                        <FaChevronDown className="text-gray-500 hover:text-gray-700" />
                    )}
                </div>
            </div>

            {/* Accordion Content */}
            {isExpanded && (
                <>
                    {!isEditing ? (
                        /* View Mode */
                        <div key="view-mode" className="flex justify-between items-start w-full bg-white p-4 border-x border-b border-neutral-200 rounded-b-lg gap-4">
                            <div className="flex items-start gap-3 flex-1 text-sm text-gray-800 min-w-0">
                                <div
                                    className="notes-view-content flex-1 break-words min-w-0"
                                    dangerouslySetInnerHTML={{ __html: initialHtml }}
                                />
                            </div>
                            <div className="flex gap-4 items-center flex-shrink-0">
                                <button
                                    type="button"
                                    className="text-blue-600 hover:text-blue-900 flex items-center gap-2 font-medium"
                                    onClick={handleStartEdit}
                                >
                                    <FaRegEdit size={18} /> Edit Note
                                </button>
                                <button
                                    type="button"
                                    className="text-red-600 hover:text-red-900 flex items-center"
                                    onClick={handleDeleteClick}
                                    title="Delete Note"
                                >
                                    <FaTrash size={18} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Edit Mode */
                        <div key="edit-mode" className="notes-editor-container border-x border-b border-gray-200">
                            {/* Formatting Toolbar */}
                            <div className="notes-editor-toolbar flex flex-wrap justify-between items-center bg-gray-50 border-b border-gray-200 px-4 py-2">
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Bold"
                                        onMouseDown={(e) => handleFormat(e, 'bold')}
                                    >
                                        <FaBold />
                                    </button>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Italic"
                                        onMouseDown={(e) => handleFormat(e, 'italic')}
                                    >
                                        <FaItalic />
                                    </button>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Underline"
                                        onMouseDown={(e) => handleFormat(e, 'underline')}
                                    >
                                        <FaUnderline />
                                    </button>

                                    <div className="notes-toolbar-separator" />

                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Bullet List"
                                        onMouseDown={(e) => handleFormat(e, 'insertUnorderedList')}
                                    >
                                        <FaListUl />
                                    </button>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Numbered List"
                                        onMouseDown={(e) => handleFormat(e, 'insertOrderedList')}
                                    >
                                        <FaListOl />
                                    </button>

                                    <div className="notes-toolbar-separator" />

                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Align Left"
                                        onMouseDown={(e) => handleFormat(e, 'justifyLeft')}
                                    >
                                        <FaAlignLeft />
                                    </button>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Align Center"
                                        onMouseDown={(e) => handleFormat(e, 'justifyCenter')}
                                    >
                                        <FaAlignCenter />
                                    </button>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Align Right"
                                        onMouseDown={(e) => handleFormat(e, 'justifyRight')}
                                    >
                                        <FaAlignRight />
                                    </button>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn"
                                        title="Justify"
                                        onMouseDown={(e) => handleFormat(e, 'justifyFull')}
                                    >
                                        <FaAlignJustify />
                                    </button>
                                </div>

                                <div>
                                    <button
                                        type="button"
                                        className="notes-toolbar-btn text-red-600 hover:bg-red-50 hover:text-red-900"
                                        title="Clear Note"
                                        onMouseDown={handleClearClick}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>

                            {/* Rich text editing area */}
                            <div
                                ref={editorRef}
                                className="notes-editor-content focus:ring-0 focus:outline-none"
                                contentEditable={true}
                                data-placeholder={placeholder}
                                onInput={handleInput}
                                onBlur={handleInput}
                            />

                            {/* Footer */}
                            <div className="notes-editor-footer border-t border-gray-100 px-4 py-3 bg-gray-50 flex justify-end items-center">
                                <div className="notes-actions flex gap-2">
                                    <ActionButton
                                        className="!bg-white !text-gray-700 !border !border-solid !border-black !h-10 px-6 rounded flex items-center justify-center font-medium shadow-none"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </ActionButton>
                                    <Button
                                        className={`!h-10 !px-6 rounded font-medium flex items-center justify-center shadow-none ${(isSaving || !isDirty)
                                            ? '!bg-gray-300 !text-gray-500 cursor-not-allowed'
                                            : '!bg-gray-900 !text-white hover:!bg-gray-800'
                                            }`}
                                        onClick={handleSave}
                                        disabled={isSaving || !isDirty}
                                    >
                                        {isSaving ? "Saving..." : "Save note"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default NotesParentsSection;
